import {useDirection} from '@base-ui/react/direction-provider';
import {Menu as MenuPrimitive} from '@base-ui/react/menu';

import {cn} from '../../lib/utils';
import {Icon} from '../Icon/Icon';

function DropdownMenu({...props}: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuTrigger({...props}: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

function DropdownMenuContent({
  align = 'start',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  className,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>) {
  const direction = useDirection();
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        className="isolate z-50 outline-none"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        dir={direction}
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            'z-50 flex max-h-(--available-height) min-w-40 origin-(--transform-origin) flex-col overflow-x-hidden overflow-y-auto rounded-lg bg-background-surface p-1.5 text-sm text-fg shadow-md ring-1 ring-fg/10 outline-none duration-100',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-start-2 data-[side=inline-start]:slide-in-from-end-2 data-[side=top]:slide-in-from-bottom-2',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

const itemClassName = [
  'relative flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-start text-sm text-fg outline-hidden select-none',
  'data-highlighted:bg-background-surface_hover',
  'data-disabled:cursor-not-allowed data-disabled:opacity-55',
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
].join(' ');

function DropdownMenuItem({
  className,
  variant = 'default',
  ...props
}: MenuPrimitive.Item.Props & {variant?: 'default' | 'destructive'}) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-variant={variant}
      className={cn(
        itemClassName,
        'data-[variant=destructive]:text-destructive data-[variant=destructive]:data-highlighted:bg-background-error',
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuRadioGroup({...props}: MenuPrimitive.RadioGroup.Props) {
  return <MenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
}

/** A radio item with its check at the start, so multi-line children (a label and a description) stay aligned. */
function DropdownMenuRadioItem({className, children, ...props}: MenuPrimitive.RadioItem.Props) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(itemClassName, 'items-start', className)}
      {...props}
    >
      <span
        data-slot="dropdown-menu-radio-item-indicator"
        className="mt-0.5 flex size-4 shrink-0 items-center justify-center text-brand-accent"
      >
        <MenuPrimitive.RadioItemIndicator>
          <Icon name="check" className="size-4" aria-hidden="true" />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  );
}

function DropdownMenuSeparator({className, ...props}: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('-mx-1.5 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
};
